<template>
  <section class="jobs-overview">
    <content-area
      v-if="jobsDefaultTopContent && jobsDefaultTopContent.length > 0"
      :content="jobsDefaultTopContent"
    ></content-area>

    <content-area
      v-if="content && jobsContentPosition === 'top'"
      :content="content"
    ></content-area>

    <v-row>
      <v-col cols="12">
        <div class="jobs-overview content-box dense alternate text-left">
          <v-row dense id="jobs-overview-header" class="mb-2 fixed-menu-anchor">
            <v-col cols="12" sm="5" class="dense d-flex align-center">
              <h2 class="job-overview__item-count">
                {{ numJobs }} {{ $tc('jobs.joboffers', numJobs) }}
              </h2>
            </v-col>
            <v-col cols="12" sm="7" class="dense text-right">
              <div v-if="jobsLayoutToggle" class="button-group">
                <v-btn
                  :class="{ active: jobsLayout === 'list' }"
                  @click="setJobsLayout('list')"
                  :title="$t('jobs.listView')"
                  :aria-label="$t('jobs.listView')"
                >
                  <v-icon>mdi-view-headline</v-icon>
                </v-btn>

                <v-btn
                  :class="{ active: jobsLayout === 'tiles' }"
                  @click="setJobsLayout('tiles')"
                  :title="$t('jobs.gridView')"
                  :aria-label="$t('jobs.gridView')"
                >
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
              </div>

              <jobalert-dialog></jobalert-dialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              :md="colsMd"
              :lg="colsLg"
              v-for="job in visibleJoboffers"
              :key="`joboffer_item_${job.jobofferId}`"
            >
              <component :is="jobItemComponent" :joboffer="job"></component>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>

    <content-area
      v-if="content && jobsContentPosition === 'bottom'"
      :content="content"
    ></content-area>

    <content-area
      v-if="jobsDefaultBottomContent && jobsDefaultBottomContent.length > 0"
      :content="jobsDefaultBottomContent"
    ></content-area>
  </section>
</template>

<script>
import jobofferListItem from '@/components/jobs/joboffer-list-item.vue';
import jobofferTileItem from '@/components/jobs/joboffer-tile-item.vue';
import contentArea from '@/components/content/content-area.vue';
import jobalertDialog from '@/components/jobalert/signup-dialog.vue';

import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';
import TrackingService from '@/services/TrackingService.js';

export default {
  data: () => ({
    filterTrackingDebounce: null,
    lastTrackedFilterState: ''
  }),

  mounted() {
    if (this.isInternal && this.$route.name === 'jobsInternal') {
      this.$router.push({ name: 'jobs' });
      return;
    }

    if (this.defaultClaim && this.defaultClaim.length > 0) {
      this.setHeroClaim(this.defaultClaim);
    }

    const searchParams = this.$route.query;
    if (searchParams['show'] === 'all') {
      this.clearFilters();
    }

    if (searchParams['company'] || searchParams['c']) {
      this.setFilters(
        'companyId',
        searchParams['company'] || searchParams['c']
      );
    }

    if (searchParams['category']) {
      this.setFilters('categoryId', searchParams['category']);
    }

    if (searchParams['department']) {
      this.setFilters('departmentId', searchParams['department']);
    }

    if (searchParams['contract-type'] || searchParams['ct']) {
      this.setFilters(
        'contractTypeId',
        searchParams['contract-type'] || searchParams['ct']
      );
    }

    if (searchParams['career-level'] || searchParams['cl']) {
      this.setFilters(
        'careerLevelId',
        searchParams['career-level'] || searchParams['cl']
      );
    }
  },

  beforeDestroy() {
    this.clearFilterTrackingDebounce();
  },

  computed: {
    ...mapState({
      routeImages: 'routeImages'
    }),

    ...mapState('jobsModule', {
      filters: 'filters',
      jobofferItems: 'joboffers'
    }),

    ...mapState('companyModule', {
      isInternal: 'isInternal',
      jobsContentPosition: 'jobsContentPosition',
      jobsDefaultContentPosition: 'jobsDefaultContentPosition',
      jobsDefaultTopContent: 'jobsDefaultTopContent',
      jobsDefaultBottomContent: 'jobsDefaultBottomContent',
      jobsLayout: 'jobsLayout',
      defaultClaim: 'defaultClaim',
      jobsLayoutToggle: 'jobsLayoutToggle',
      showBlacklistedJoboffers: 'showBlacklistedJoboffers'
    }),

    ...mapGetters('jobsModule', {
      joboffers: 'getJoboffers',
      content: 'getContent',
      careerLevels: 'getCareerLevels',
      categories: 'getCategories',
      companies: 'getCompanies',
      contractTypes: 'getContractTypes',
      costCenters: 'getCostCenters',
      departments: 'getDepartments',
      locations: 'getLocations',
      tempJobs: 'getTempJobs',
      workingTimes: 'getWorkingTimes'
    }),

    visibleJoboffers() {
      return this.joboffers.filter(
        (x) => this.showBlacklistedJoboffers || !x.isBlacklisted
      );
    },

    colsMd() {
      if (this.jobsLayout === 'tiles') {
        return 6;
      }

      return 12;
    },

    colsLg() {
      if (this.jobsLayout === 'tiles') {
        return 6;
      }

      return 12;
    },

    jobItemComponent() {
      if (this.jobsLayout === 'tiles') {
        return 'jobofferTileItem';
      }

      return 'jobofferListItem';
    },

    numJobs() {
      return this.visibleJoboffers.length;
    },

    totalJoboffers() {
      return this.jobofferItems.filter(
        (x) => this.showBlacklistedJoboffers || !x.isBlacklisted
      ).length;
    }
  },

  watch: {
    filters: {
      handler() {
        this.scheduleFilterTracking();
      },
      deep: true
    }
  },

  created() {
    const routeImage = this.routeImages['jobs'];
    if (routeImage) {
      this.setHeroImage(routeImage);
    }
  },

  methods: {
    ...mapMutations({
      setHeroClaim: 'setHeroClaim',
      setHeroImage: 'setHeroImage'
    }),

    ...mapMutations('companyModule', {
      setJobsLayout: 'setJobsLayout'
    }),

    ...mapActions('jobsModule', {
      clearFilters: 'clearFilters',
      setFilter: 'setFilter'
    }),

    clearFilterTrackingDebounce() {
      if (this.filterTrackingDebounce) {
        clearTimeout(this.filterTrackingDebounce);
        this.filterTrackingDebounce = null;
      }
    },

    scheduleFilterTracking() {
      this.clearFilterTrackingDebounce();

      this.filterTrackingDebounce = setTimeout(() => {
        this.filterTrackingDebounce = null;

        this.$nextTick(() => {
          this.trackFilterChange();
        });
      }, 250);
    },

    trackFilterChange() {
      const filters = this.getTrackingFilters();
      const serializedFilters = JSON.stringify(filters);

      if (serializedFilters === this.lastTrackedFilterState) {
        return;
      }

      this.lastTrackedFilterState = serializedFilters;
      TrackingService.trackFilterChange(
        filters,
        this.visibleJoboffers.length,
        this.totalJoboffers
      );
    },

    getTrackingFilters() {
      const fieldNames = {
        companyId: 'company',
        categoryId: 'category',
        departmentId: 'department',
        contractTypeId: 'contractType',
        careerLevelId: 'careerLevel',
        locationId: 'location',
        workingTimeId: 'workingTime',
        costCenterId: 'costCenter',
        tempJobId: 'tempJob',
        keyword: 'keyword'
      };

      const result = {};
      for (const [field, filters] of Object.entries(this.filters || {})) {
        if (!filters || filters.length === 0) {
          continue;
        }

        const trackingField = fieldNames[field] || field;
        result[trackingField] = filters.map((filter) => ({
          value: filter.value,
          label: this.getTrackingFilterLabel(field, filter)
        }));
      }

      return result;
    },

    getTrackingFilterLabel(field, filter) {
      const localizedName = this.localize(filter.name);
      if (localizedName) {
        return localizedName;
      }

      const option = this.getTrackingFilterOptions(field).find(
        (x) => x.id === filter.value
      );
      const localizedOptionName = this.localize(option?.name);
      if (localizedOptionName) {
        return localizedOptionName;
      }

      return filter.value?.toString?.() ?? '';
    },

    getTrackingFilterOptions(field) {
      const options = {
        careerLevelId: this.careerLevels,
        categoryId: this.categories,
        companyId: this.companies,
        contractTypeId: this.contractTypes,
        costCenterId: this.costCenters,
        departmentId: this.departments,
        locationId: this.locations,
        tempJobId: this.tempJobs,
        workingTimeId: this.workingTimes
      };

      return options[field] || [];
    },

    setFilters(fieldName, filterValue) {
      for (const item of filterValue.split(',')) {
        const value = parseInt(item);
        const matchingOption = this.getTrackingFilterOptions(fieldName).find(
          (x) => x.id === value
        );
        const filter = {
          field: fieldName,
          value,
          name: matchingOption?.name
        };

        this.setFilter(filter);
        eventBus.$emit('filterAdded', filter);
      }
    }
  },

  components: {
    contentArea,
    jobalertDialog,
    jobofferListItem,
    jobofferTileItem
  }
};
</script>
<style scoped>
h2.job-overview__item-count {
  font-family: var(--font-family) !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  color: var(--font-color) !important;
  margin-bottom: 0 !important;
}
</style>
